import { useEffect, useRef, type RefObject } from 'react';

/**
 * Tracks pointer position in a ref rather than React state, so callers can
 * read it inside their own rAF loop without triggering a re-render per
 * pointermove — the cheap pattern docs/DESIGN.md §5/§7 asks for. Gate on
 * `pointer: fine` at the call site before using this for any visual effect.
 */
export function usePointerPosition(): RefObject<{ x: number; y: number } | null> {
  const ref = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      ref.current = { x: e.clientX, y: e.clientY };
    };
    const onLeave = () => {
      ref.current = null;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return ref;
}
